/**
 * Fetch wrapper that automatically adds CSRF tokens for state-changing requests
 * Use this for any fetch() calls to Express API endpoints
 */

import supabase from './supabaseClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// CSRF token cache
let csrfToken = null;
let csrfTokenExpiry = null;

/**
 * Refresh the Supabase access token and sync it into localStorage.auth_token
 * (the custom key this wrapper sends as the Bearer). The session token rotates
 * ~hourly; this custom key isn't auto-updated, so a long-running flow (e.g.
 * onboarding) can start sending a stale token and get 401s. Called on a 401 to
 * recover transparently. Returns the fresh token, or null if no session.
 */
async function refreshAuthToken() {
  try {
    const { data } = await supabase.auth.refreshSession();
    const token = data?.session?.access_token;
    if (token) { try { localStorage.setItem('auth_token', token); } catch (_) {} return token; }
  } catch (_) { /* fall through to getSession */ }
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) { try { localStorage.setItem('auth_token', token); } catch (_) {} return token; }
  } catch (_) {}
  return null;
}

/**
 * Fetch CSRF token from the server
 * Caches the token until it expires
 */
async function getCsrfToken() {
  // Return cached token if still valid (with 5 min buffer)
  if (csrfToken && csrfTokenExpiry && Date.now() < csrfTokenExpiry) {
    console.log('🔒 Using cached CSRF token');
    return csrfToken;
  }

  try {
    const authToken = localStorage.getItem('auth_token');
    // CSRF endpoint is at /api/csrf-token from server root
    const csrfUrl = API_BASE.includes('/api')
      ? `${API_BASE}/csrf-token`
      : `${API_BASE}/api/csrf-token`;

    console.log('🔑 Fetching CSRF token from:', csrfUrl);
    const response = await fetch(csrfUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('❌ Failed to fetch CSRF token, status:', response.status);
      const errorText = await response.text();
      console.error('❌ CSRF token error body:', errorText);
      return null;
    }

    const data = await response.json();
    csrfToken = data.csrfToken;
    // Set expiry to 5 minutes before actual expiry for safety
    csrfTokenExpiry = Date.now() + ((data.expiresIn - 300) * 1000);

    console.log('✅ CSRF token fetched successfully');
    return csrfToken;
  } catch (error) {
    console.error('❌ Error fetching CSRF token:', error);
    return null;
  }
}

/**
 * Fetch wrapper with automatic CSRF token handling
 * Automatically retries once with a fresh token on 403 Invalid CSRF token errors.
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export async function fetchWithCsrf(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isStateChanging = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  async function doFetch(forceRefresh = false) {
    const headers = { ...options.headers };

    // Add auth token if available
    const authToken = localStorage.getItem('auth_token');
    if (authToken && !headers.Authorization) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    // Add CSRF token for state-changing methods
    if (isStateChanging) {
      if (forceRefresh) {
        csrfToken = null;
        csrfTokenExpiry = null;
      }
      const csrf = await getCsrfToken();
      if (csrf) {
        headers['X-CSRF-Token'] = csrf;
      } else {
        console.warn('⚠️ No CSRF token available for request!');
      }
    }

    return fetch(url, { ...options, headers });
  }

  let response = await doFetch(false);

  // On an expired/stale auth token (401), refresh the Supabase session once and
  // retry transparently — so a long onboarding never dies on a rotated token.
  if (response.status === 401) {
    const fresh = await refreshAuthToken();
    if (fresh) response = await doFetch(false); // doFetch re-reads the updated token
    return response;
  }

  // On CSRF failure, clear cache and retry once with a fresh token
  if (isStateChanging && response.status === 403) {
    let body;
    try {
      body = await response.clone().json();
    } catch (_) {}
    if (body?.error === 'Invalid CSRF token' || body?.error === 'CSRF token missing') {
      console.warn('⚠️ CSRF token rejected — refreshing and retrying...');
      return doFetch(true);
    }
  }

  return response;
}

export { getCsrfToken };
export default fetchWithCsrf;
